import { useMemo } from "react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { useAuth } from "../../../hooks/useAuth";
import { useCampaignPerformanceQuery } from "../../../hooks/useCampaignPerformanceQuery";
import { useAirlinesCampaignPerformanceQuery } from "../../../hooks/useAirlinesCampaignPerformanceQuery";
import { useSelectedAirline } from "../../../hooks/useSelectedAirline";
import { ChartNoData } from "../ChartNoData";
import { SearchCoverageChart, type SearchCoverageAirlineSeries } from "./SearchCoverageChart";

export type SingleSeriesSearchCoverageLoaderProps = {
  from: Date;
  to: Date;
};

export const SingleSeriesSearchCoverageLoader = ({ from, to }: SingleSeriesSearchCoverageLoaderProps) => {
  const i18n = useI18n();
  const { isAirlineUser } = useAuth();
  const useQuery = isAirlineUser ? useCampaignPerformanceQuery : useAirlinesCampaignPerformanceQuery;
  const { selectedAirline } = useSelectedAirline({ useSuspense: true });
  const { data: metrics } = useQuery({
    from,
    to,
    metrics: ["SEARCH_COVERAGE_PERCENTAGE"],
    airlineCode: selectedAirline?.code,
  });

  const series: SearchCoverageAirlineSeries[] = useMemo(() => {
    const searchCoverageMetric = metrics.find(
      (metric) => metric.metadata.metricName === "SEARCH_COVERAGE_PERCENTAGE"
    );

    if (!searchCoverageMetric) {
      return [];
    }

    return [
      {
        airlineCode: selectedAirline?.code ?? "",
        airlineName: selectedAirline?.name ?? "",
        metric: searchCoverageMetric,
      },
    ];
  }, [selectedAirline, metrics]);

  if (!series.length) {
    return <ChartNoData titleText={i18n.trans(t("flights_airline_portal_realtime_performance_metric_three_name"))} />;
  }

  return <SearchCoverageChart series={series} />;
};
